/**
 * Asset metadata.
 *
 * Binary data lives in Azure Blob Storage (private containers only), metadata
 * lives in Cosmos DB. The reference documentation contains 374 images on 58
 * pages - not a single page without one - so assets are a core concern, not an
 * add-on (see reference-document-decisions.md, RD-08).
 *
 * Consequences the model enforces:
 * - the blob path is generated from ids, never from the uploaded file name
 * - the original file name is kept for display and download only
 * - one binary is referenced by many blocks; it is never stored twice
 * - malware scanning is a first class state, not an afterthought
 * - `sharedWithCustomer` is an explicit per-asset opt-in
 * - `checksum` allows a published version to pin exactly this binary
 */
export const BLOB_CONTAINERS = [
    'customer-assets',
    'document-exports',
    'source-imports',
    'temporary-files',
];
/**
 * What the asset IS. Derived from the three clearly distinguishable image roles
 * in the reference (situation photo, floor/site plan, measurement screenshot)
 * plus the roles needed by the platform itself.
 */
export const ASSET_ROLES = [
    'photo',
    'plan',
    /** Small preview of a plan image, owned by the same floor plan. */
    'planThumbnail',
    /** Image placed in a document by hand (design 2026-10-31), owned by the document. */
    'documentImage',
    'screenshot',
    'symbol',
    'signature',
    'attachment',
    'sourceImport',
    'export',
];
export const ASSET_SCAN_STATUSES = ['pending', 'clean', 'infected', 'failed', 'skipped'];
export const ASSET_STATUSES = ['active', 'quarantined', 'blocked', 'replaced', 'deleted'];
/** Where the file came from - relevant for licence and traceability questions. */
export const ASSET_SOURCES = ['upload', 'wordImport', 'generated', 'externalSystem'];
/** Upload constraints. Enforced server side before a blob is written. */
export const ASSET_LIMITS = {
    maxImageBytes: 20 * 1024 * 1024,
    maxAttachmentBytes: 50 * 1024 * 1024,
    allowedImageMediaTypes: ['image/png', 'image/jpeg', 'image/webp'],
    /**
     * SVG is deliberately NOT in the allow-list: it can carry scripts. Whether to
     * sanitise or to forbid it is [OFFEN] B2 in docs/open-decisions.md.
     */
    allowedAttachmentMediaTypes: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'application/zip',
    ],
};
/** An asset may only be rendered when it is clean and active. Fail closed. */
export function isDeliverable(asset, audience) {
    if (asset.status !== 'active' && asset.status !== 'replaced') {
        return false;
    }
    if (asset.malwareScanStatus !== 'clean' && asset.malwareScanStatus !== 'skipped') {
        return false;
    }
    return audience === 'internal' ? true : asset.sharedWithCustomer;
}
//# sourceMappingURL=asset.js.map